import { useCallback, useEffect, useState } from 'react';

enum CollapseStage {
    Expanded = 'expanded',
    Partial = 'partial',
    Collapsed = 'collapsed',
}

const STAGE_ORDER = [CollapseStage.Expanded, CollapseStage.Partial, CollapseStage.Collapsed];

const readStage = (key: string): CollapseStage => {
    const stored = window.localStorage.getItem(key);

    return STAGE_ORDER.find((stage) => stage === stored) ?? CollapseStage.Expanded;
};

const useLocalStorageCollapseStage = (localStorageKey: string) => {
    const [stage, setStage] = useState<CollapseStage>(() => readStage(localStorageKey));

    useEffect(() => {
        window.localStorage.setItem(localStorageKey, stage);
    }, [stage, localStorageKey]);

    const cycleStage = useCallback(() => {
        setStage((current) => STAGE_ORDER[(STAGE_ORDER.indexOf(current) + 1) % STAGE_ORDER.length]);
    }, []);

    return { stage, setStage, cycleStage } as const;
};

const useLocalStorageCollapseState = (localStorageKey: string) => {
    const [filtersCollapsed, setFiltersCollapsed] = useState(
        () => readStage(localStorageKey) === CollapseStage.Collapsed,
    );

    useEffect(() => {
        window.localStorage.setItem(
            localStorageKey,
            filtersCollapsed ? CollapseStage.Collapsed : CollapseStage.Expanded,
        );
    }, [filtersCollapsed, localStorageKey]);

    return [filtersCollapsed, setFiltersCollapsed] as const;
};

export { CollapseStage, useLocalStorageCollapseStage, useLocalStorageCollapseState };
